// Apollo
import { apolloClient } from '@/graphql';
import gql from 'graphql-tag';

// GraphQL
import UpdatePitch from '@/graphql/pitches/update/updatePitch.gql';
import InsertPitches from '@/graphql/pitches/insert/insertPitches.gql';
import DeletePitch from '@/graphql/pitches/delete/deletePitch.gql';
import InsertEvents from '@/graphql/events/insert/insertEvents.gql';
import UpdateEvents from '@/graphql/events/update/updateEvents.gql';
import updateMultipleEvents from '@/graphql/events/update/updateMultipleEvents.gql';
import DeleteEvent from '@/graphql/events/delete/deleteEvent.gql';
import InsertBookings from '@/graphql/bookings/insertBookings.gql';
import UpdateCustomerBookings from '@/graphql/bookings/updateCustomerBookings.gql';
import UpdateMultipleBookings from '@/graphql/bookings/updateMultipleBookings.gql';
import DeleteCustomerBooking from '@/graphql/bookings/deleteCustomerBooking.gql';
import DeleteplayerBooking from '@/graphql/players/delete/removePlayerBooking.gql';
import SetPlayerBookingCancelled from '@/graphql/players/update/SetPlayerBookingCancelled.gql';
import SetPlayerBookingNew from '@/graphql/players/update/SetPlayerBookingNew.gql';
import UpdatePlayersBookings from '@/graphql/bookings/updatePlayerBookings.gql';
import InsertDeletedDate from '@/graphql/dates/insertDeletedDates.gql';
import InsertIgnoredDate from '@/graphql/dates/insertIgnoredDates.gql';

// UUID
import { v4 as uuidv4 } from 'uuid';

export default {
	state: {
		pitches: [] as HasuraPitch[],
	},

	// ================ Getters ================
	getters: {
		pitches(state: any) {
			return state.pitches;
		},
	},

	// ================ Mutations ================
	mutations: {
		setPitches(state: any, pitches: any) {
			state.pitches = pitches;
		},
	},

	// ================= Actions =================
	actions: {
		async getPitches({ commit }: any) {
			const response = await apolloClient.query({
				query: gql`
					query Pitches {
						pitches {
							id
							name
							pitch_name
							type
							location_id
							sunFrom
							sunTo
							monFrom
							monTo
							tueFrom
							tueTo
							wedFrom
							wedTo
							thursFrom
							thursTo
							friFrom
							friTo
							satFrom
							satTo
							sunPrice
							monPrice
							tuePrice
							wedPrice
							thursPrice
							friPrice
							satPrice
							location {
								id
								name
								city_id
							}
						}
					}
				`,
				fetchPolicy: 'no-cache',
			});

			const { pitches } = response.data;
			commit('setPitches', pitches);
		},

		async insertPitches({ dispatch }: any, payload: any) {
			const objects = payload.map((p: any) => {
				return {
					name: p.name,
					pitch_name: p.pitch_name,
					location_id: p.locationId,
					type: p.type,
				};
			});

			await apolloClient
				.mutate({
					mutation: InsertPitches,
					// Parameters
					variables: {
						objects,
					},
				})
				.catch(err => {
					console.debug(err);
				});

			await dispatch('getPitches');
			await dispatch('getLocations');
			return;
		},

		async insertPitchesV2({ dispatch }: any, objects: any) {
			await apolloClient
				.mutate({
					mutation: InsertPitches,
					// Parameters
					variables: {
						objects,
					},
				})
				.catch(err => {
					console.debug(err);
				});

			await dispatch('getPitches');
			await dispatch('getLocations');
			return;
		},

		async deletePitch({ dispatch }: any, payload: any) {
			const { id } = payload;
			await apolloClient
				.mutate({
					mutation: DeletePitch,
					// Parameters
					variables: {
						pitchId: id,
					},
				})
				.catch(err => {
					console.debug(err);
				});

			await dispatch('getPitches');
			await dispatch('getLocations');
			return;
		},

		async updatePitch({ dispatch }: any, payload: any) {
			const { id, name, type } = payload;
			await apolloClient.mutate({
				mutation: UpdatePitch,
				// Parameters
				variables: {
					pitchId: id,
					name,
					type,
				},
			});

			await dispatch('getPitches');
			await dispatch('getLocations');
			return;
		},

		async getAllPitchesFromLocation(_: any, locationId: string) {
			const { data } = await apolloClient.query({
				query: gql`
					query getPitchesByVenue($locationId: uuid!) {
						pitches(where: { location_id: { _eq: $locationId } }) {
							id
							name
							monFrom
							monTo
							tueFrom
							tueTo
							wedFrom
							wedTo
							thursFrom
							thursTo
							friFrom
							friTo
							satFrom
							satTo
							sunFrom
							sunTo
						}
					}
				`,
				variables: {
					locationId,
				},
			});
			return data.pitches;
		},
		async getReservedEvents(_: any, locationId: string) {
			const response = await apolloClient.query({
				query: gql`
					query GetReservedEvents($locationId: uuid!) {
						reserved_events(where: { location_id: { _eq: $locationId } }) {
							id
							date
							field
							created_by
							duration
							frequency
							time_from
							time_to
							price
							payment_method
							title
							comment
							is_public
							location_id
							is_holiday
							discount_type
							discount
							total_price
							deleted_dates: deleted_dates {
								id
								date
								event_id
							}
							ignored_dates: ignored_dates {
								id
								date
								event_id
							}
						}
					}
				`,
				variables: {
					locationId,
				},
				fetchPolicy: 'no-cache',
			});
			const events = response.data.reserved_events || [];
			return events;
		},
		async insertEvents({ dispatch }: any, payload: any) {
			console.log("pyload for inserting reserved", payload)
			const objects = [
				{
					id: payload.id,
					date: payload.date,
					field: payload.field,
					created_by: payload.created_by,
					duration: payload.duration,
					time_from: payload.time_from,
					time_to: payload.time_to,
					frequency: payload.frequency ? payload.frequency.toString() : null,
					title: payload.title,
					comment: payload.comment,
					is_public: payload.is_public,
					location_id: payload.location_id,
					price: payload.price,
					is_holiday: payload.is_holiday,
					discount_type: payload.discount_type,
					discount: payload.discount,
					total_price: payload.total_price,
				},
			];
			const response = await apolloClient
				.mutate({
					mutation: InsertEvents,
					// Parameters
					variables: {
						objects,
					},
				})
				.catch(err => {
					console.debug(err);
				});
			return response;
		},
		async updateEvents({ dispatch }: any, payload: any) {
			let {
				id,
				date,
				field,
				created_by,
				duration,
				time_from,
				time_to,
				frequency,
				title,
				comment,
				is_public,
				price,
				discount,
				discount_type,
				total_price,
			} = payload;
			frequency = frequency || null;
			await apolloClient.mutate({
				mutation: UpdateEvents,
				// Parameters
				variables: {
					eventId: id,
					date,
					field,
					created_by,
					duration,
					time_from,
					time_to,
					frequency,
					title,
					comment,
					is_public,
					price,
					discount,
					discount_type,
					total_price,
				},
			});
			return;
		},
		async deleteEvent({ dispatch }: any, eventId: any) {
			await apolloClient
				.mutate({
					mutation: DeleteEvent,
					// Parameters
					variables: {
						eventId,
					},
				})
				.catch(err => {
					console.debug(err);
				});
			return;
		},
		async deleteCustomerBooking({ dispatch }: any, eventId: any) {
			await apolloClient
				.mutate({
					mutation: DeleteCustomerBooking,
					// Parameters
					variables: {
						eventId,
					},
				})
				.catch(err => {
					console.debug(err);
				});
			return;
		},
		async setPlayerBookingCancelled(_: any, payload: any) {
			console.log('payload', payload);
			const { data } = await apolloClient.mutate({
				// @ts-ignore
				mutation: SetPlayerBookingCancelled,
				variables: {
					date: payload.date,
					location_id: payload.location_id,
					player_booking_id: payload.player_booking_id,
					display_date: payload.display_date,
					price: payload.price,
					pitchId: payload.pitchId,
					pitch_id: payload.pitch_id,
					pitch_type: payload.pitch_type,
					split: payload.split,
					time_from: payload.time_from,
					time_to: payload.time_to,
					created_at: payload.bookedOn,
					players: payload.players,
					booker: payload.booker,
					venu: payload.venu,
					payment: payload.payment,
					city_id: payload.city_id,
					country: payload.country,
					email: payload.email,
					edited_on: payload.edited_on,
				},
			});
			return data;
		},
		async deleteplayerBooking({ dispatch }: any, eventId: any) {
			// @ts-ignore
			const { data } = await apolloClient
				.mutate({
					mutation: DeleteplayerBooking,
					// Parameters
					variables: {
						eventId,
					},
				})
				.catch(err => {
					console.debug(err);
				});
			return data;
		},
		async updateMultipleEvents({ commit, dispatch }: any, events: any[]) {
			const promises = events.map(async (event: any) => {
				try {
					return apolloClient.mutate({
						mutation: updateMultipleEvents,
						// Parameters
						variables: {
							eventId: event.id,
							object: event,
						},
					});
				} catch (err) {
					console.debug(err);
				}
			});

			try {
				const responses = await Promise.all([...promises]);
				const response = responses.map((res: any) => {
					return res.data;
				});

				return response;
			} catch (error) {
				return error;
			}
		},

		async getCustomerBookings(_: any, locationId: string) {
			const response = await apolloClient.query({
				query: gql`
					query GetCustomerBookings($locationId: uuid!) {
						customer_bookings(where: { location_id: { _eq: $locationId } }) {
							id
							date
							field
							created_by
							duration
							frequency
							time_from
							time_to
							price
							payment_method
							title
							comment
							is_public
							location_id
							customer_id
							discount
							discount_type
							total_price
							new_player
							phone_number
							deleted_dates: deleted_dates {
								id
								date
								event_id
							}
							ignored_dates: ignored_dates {
								id
								date
								event_id
							}
							customer: customer {
								id
								first_name
								last_name
								phone_number
								email
							}
						}
					}
				`,
				variables: {
					locationId,
				},
				fetchPolicy: 'no-cache',
			});
			const events = response.data.customer_bookings || [];
			return events;
		},

		async getPlayerBookings(_: any, locationId: string) {
			const response = await apolloClient.query({
				query: gql`
					query GetPlayerBookings($locationId: uuid!) {
						player_booking_schedules(where: { location_id: { _eq: $locationId } }) {
							time_to
							time_from
							split
							price
							player_booking_id
							pitch_type
							pitch_id
							pitchId
							location_id
							id
							display_date
							date
							comment
							location {
								incremental_time
								minimum_duration
							}
							player_booking {
								payment_type
								player {
									last_name
									first_name
								}
							}
						}
					}
				`,
				variables: {
					locationId,
				},
				fetchPolicy: 'no-cache',
			});
			const events = response.data.player_booking_schedules || [];
			return events;
		},
		async getcustomerBookingbyCustomeId(_: any, id: string) {
			const response = await apolloClient.query({
				query: gql`
					query GetPlayerBookings($id: uuid!) {
						customer_bookings(where: { id: { _eq: $id } }) {
							id
							date
							field
							created_by
							duration
							frequency
							time_from
							time_to
							price
							created_at
							payment_method
							title
							comment
							is_public
							location_id
							customer_id
							discount
							discount_type
							location_id
							total_price
							pitch {
								field_size
								type
								location {
									address
									name
									city {
										id
										country_id
									}
								}
							}
						}
					}
				`,
				variables: {
					id,
				},
				fetchPolicy: 'no-cache',
			});
			if (response.data) return response.data.customer_bookings;
			else return response;
		},
		async getplayerBookingbyId(_: any, player_booking_id: any) {
			const response = await apolloClient.query({
				query: gql`
					query GetPlayerBookings($player_booking_id: Int!) {
						player_booking_schedules(where: { player_booking_id: { _eq: $player_booking_id } }) {
							date
							display_date
							id
							created_at
							pitch_id
							pitch_type
							player_booking_id
							price
							split
							time_from
							time_to
							comment
							payment_id
							player_booking {
								player {
									email
								}
							}
							pitch {
								location {
									name
									city {
										id
										country_id
									}
								}
							}
						}
					}
				`,
				variables: {
					player_booking_id,
				},
				fetchPolicy: 'no-cache',
			});
			console.log('response', response);
			if (response.data) return response.data.player_booking_schedules[0];
			else return response;
		},
		async SetPlayerBookings(_: any, payload: any) {
			console.log('payload', payload);
			const response = await apolloClient
				.mutate({
					mutation: SetPlayerBookingNew,
					variables: {
						id: payload.id,
						time_from: payload.time_from,
						time_to: payload.time_to,
						price: payload.price,
						date: payload.date,
						split: payload.split,
						display_date: payload.display_date,
						comment: payload.comment,
						edited_on: payload.edited_on
					},
				})
				.catch(err => {
					console.debug(err);
				});
			console.log('SetPlayerBookings', response);
			return response;
		},

		async insertCustomerBookings({ dispatch }: any, payload: any) {
			console.log(payload, "payload")
			const objects = [
				{
					id: payload.id,
					date: payload.date,
					field: payload.field,
					created_by: payload.created_by,
					duration: payload.duration,
					time_from: payload.time_from,
					time_to: payload.time_to,
					frequency: payload.frequency.toString() || null,
					title: payload.title,
					comment: payload.comment,
					is_public: payload.is_public,
					location_id: payload.location_id,
					customer_id: payload.customer_id ? payload.customer_id : null,
					price: payload.price,
					payment_method: payload.payment_method,
					discount_type: payload.discount_type,
					discount: payload.discount,
					total_price: payload.total_price,
					new_player: payload.new_player,
					phone_number: payload.phone_number,
				},
			];
			const response = await apolloClient
				.mutate({
					mutation: InsertBookings,
					// Parameters
					variables: {
						objects,
					},
				})
				.catch(err => {
					console.debug(err);
				});
			return response;
		},

		async updateCustomerBookings({ dispatch }: any, payload: any) {
			let {
				id,
				date,
				field,
				created_by,
				duration,
				time_from,
				time_to,
				frequency,
				title,
				comment,
				is_public,
				customer_id,
				price,
				payment_method,
				discount,
				discount_type,
				total_price,
				new_player,
				edited_on
			} = payload;
			frequency = frequency || null;
			await apolloClient.mutate({
				mutation: UpdateCustomerBookings,
				// Parameters
				variables: {
					eventId: id,
					date,
					field,
					created_by,
					duration,
					time_from,
					time_to,
					frequency,
					title,
					comment,
					is_public,
					customer_id,
					price,
					payment_method,
					discount,
					discount_type,
					total_price,
					new_player,
					edited_on
				},
			});
			return;
		},
		async UpdatePlayersBookings({ dispatch }: any, payload: any) {
			console.log('this.Payload', payload);
			// const promises = events.map(async (event: any) => {
			// 	try {
			// 		return apolloClient.mutate({
			// 			mutation: UpdatePlayersBookings,
			// 			// Parameters
			// 			variables: {
			// 				id: event.id,
			// 				split: event.split,
			// 				time_from: event.time_from,
			// 				time_to: event.time_to,
			// 			},
			// 		});
			// 	} catch (err) {
			// 		console.debug(err);
			// 	}
			// });

			// try {
			// 	const responses = await Promise.all([...promises]);
			// 	const response = responses.map((res: any) => {
			// 		return res.data;
			// 	});

			// 	return response;
			// } catch (error) {
			// 	return error;
			// }
			let { id, split, time_from, time_to, price, edited_on } = payload;
			await apolloClient.mutate({
				mutation: UpdatePlayersBookings,
				// Parameters
				variables: {
					id,
					split,
					time_from,
					time_to,
					price,
					edited_on
				},
			});
			return;
		},
		async updateMultipleBookings({ commit, dispatch }: any, events: any[]) {
			const promises = events.map(async (event: any) => {
				try {
					return apolloClient.mutate({
						mutation: UpdateMultipleBookings,
						// Parameters
						variables: {
							eventId: event.id,
							object: event,
						},
					});
				} catch (err) {
					console.debug(err);
				}
			});

			try {
				const responses = await Promise.all([...promises]);
				const response = responses.map((res: any) => {
					return res.data;
				});

				return response;
			} catch (error) {
				return error;
			}
		},

		async addEventDeletedDate({ dispatch }: any, payload: any) {
			const objects = [
				{
					id: uuidv4(),
					date: payload.date,
					event_id: payload.id,
				},
			];
			await apolloClient
				.mutate({
					mutation: InsertDeletedDate,
					// Parameters
					variables: {
						objects,
					},
				})
				.catch(err => {
					console.debug(err);
				});
			return;
		},
		async addEventIgnoredDate({ dispatch }: any, payload: any) {
			const objects = [
				{
					id: uuidv4(),
					date: payload.date,
					event_id: payload.event_id,
				},
			];
			await apolloClient
				.mutate({
					mutation: InsertIgnoredDate,
					// Parameters
					variables: {
						objects,
					},
				})
				.catch(err => {
					console.debug(err);
				});
			return;
		},
	},
};
